import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { CandidateCard } from "@/components/CandidateCard";
import { api } from "@/lib/api";
import { SlidersHorizontal, RotateCcw } from "lucide-react";

const OFFICE_TYPES = [
  { value: "all", label: "All office types" },
  { value: "mayor", label: "Mayor" },
  { value: "councillor", label: "Councillor" },
  { value: "trustee", label: "Trustee" },
  { value: "other", label: "Other" },
];

const BALLOT_POSITIONS = [
  { value: "all", label: "Any paper-ballot position" },
  { value: "supports", label: "Supports paper ballots" },
  { value: "custom", label: "Custom position" },
  { value: "not_stated", label: "Not stated" },
];

export default function Directory() {
  const [munis, setMunis] = useState([]);
  const [elections, setElections] = useState([]);
  const [offices, setOffices] = useState([]);
  const [filters, setFilters] = useState({ muni: "all", year: "all", type: "all", ward: "all", name: "", ballot: "all" });
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/public/municipalities"),
      api.get("/public/elections"),
      api.get("/public/offices"),
    ]).then(([m, e, o]) => {
      setMunis(m.data);
      setElections(e.data);
      setOffices(o.data);
    });
  }, []);

  const years = useMemo(() => [...new Set(elections.map((e) => e.election_year))].sort(), [elections]);
  const wards = useMemo(() => [...new Set(offices.map((o) => o.ward_label).filter(Boolean))].sort(), [offices]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.muni !== "all") params.municipality_id = filters.muni;
    if (filters.year !== "all") params.election_year = filters.year;
    if (filters.type !== "all") params.office_type = filters.type;
    if (filters.ward !== "all") params.ward = filters.ward;
    if (filters.name) params.name = filters.name;
    if (filters.ballot !== "all") params.paper_ballot_position = filters.ballot;
    const t = setTimeout(() => {
      api
        .get("/public/candidates", { params })
        .then((r) => setCandidates(r.data))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => setFilters({ muni: "all", year: "all", type: "all", ward: "all", name: "", ballot: "all" });

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="directory-title">
          Participating candidates
        </h1>
        <p className="text-muted-foreground mt-3">
          Every legitimate registered candidate can participate on equal terms. Participation is never about party,
          platform, or paper-ballot position.
        </p>
      </div>

      <Card className="mt-8">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs mb-1.5 block">Municipality</Label>
              <Select value={filters.muni} onValueChange={(v) => set("muni", v)}>
                <SelectTrigger data-testid="directory-filter-municipality">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All municipalities</SelectItem>
                  {munis.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Election year</Label>
              <Select value={String(filters.year)} onValueChange={(v) => set("year", v)}>
                <SelectTrigger data-testid="directory-filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All years</SelectItem>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Office</Label>
              <Select value={filters.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="directory-filter-office">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {OFFICE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Ward</Label>
              <Select value={filters.ward} onValueChange={(v) => set("ward", v)}>
                <SelectTrigger data-testid="directory-filter-ward">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All wards</SelectItem>
                  {wards.map((w) => (
                    <SelectItem key={w} value={w}>
                      {w}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Name</Label>
              <Input
                placeholder="Search name…"
                value={filters.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="directory-filter-name"
              />
            </div>
            <div>
              <Label className="text-xs mb-1.5 block">Paper ballots</Label>
              <Select value={filters.ballot} onValueChange={(v) => set("ballot", v)}>
                <SelectTrigger data-testid="directory-filter-ballot">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {BALLOT_POSITIONS.map((b) => (
                    <SelectItem key={b.value} value={b.value}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8">
        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        )}
        {!loading && candidates && candidates.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center">
              <p className="font-heading font-semibold text-foreground">No candidates match these filters</p>
              <p className="text-sm text-muted-foreground mt-2">Try broadening your search.</p>
              <Button variant="outline" className="mt-4" onClick={reset} data-testid="directory-reset-filters">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset filters
              </Button>
            </CardContent>
          </Card>
        )}
        {!loading && candidates && candidates.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-results">
            {candidates.map((c) => (
              <CandidateCard key={c.id} candidate={c} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
